<div class="company-page">
  <!-- Page Header -->
  <div class="page-header">
    <div class="header-content">
      <h1 class="page-title">Companies</h1>
      <p class="page-subtitle">View and manage company records</p>
    </div>
    <div class="header-actions">
      <ukg-button
        *appHasPermission="[Permissions.AccessToAddOrEditCompanies]"
        variant="primary"
        parent-icon="add"
        (click)="onAddCompany()"
        (keydown.enter)="onAddCompany()">
        Add Company
      </ukg-button>

      <!-- Search Input -->
      <div class="search-container">
        <div class="search-input-wrapper">
          <svg
            class="search-icon"
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <input
            type="text"
            class="search-input"
            placeholder="Search companies..."
            [value]="searchTerm"
            (input)="onSearchChange($any($event.target).value)"
            aria-label="Search companies" />
          @if (searchTerm) {
            <button
              class="clear-search-btn"
              (click)="clearSearch()"
              aria-label="Clear search">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          }
        </div>
      </div>
    </div>
  </div>

  <!-- Initial Loading State -->
  @if ((loading$ | async) && !hasLoadedOnce) {
    <div class="loading-container">
      <div class="loading-spinner">
        <ukg-progress-circular></ukg-progress-circular>
      </div>
      <p class="loading-text">Loading companies...</p>
    </div>
  }

  <!-- Error State -->
  @if (error$ | async; as error) {
    <div class="error-container">
      <div class="error-icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      </div>
      <h2 class="error-title">Unable to Load Companies</h2>
      <p class="error-message">{{ error }}</p>
      <button class="retry-button" (click)="onRetry()">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round">
          <polyline points="23 4 23 10 17 10"></polyline>
          <polyline points="1 20 1 14 7 14"></polyline>
          <path
            d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
        </svg>
        Try Again
      </button>
    </div>
  }

  <!-- Data Grid -->
  @if (companies$ | async; as pagedResult) {
    @if ((error$ | async) === null) {
      <!-- Empty State - No Results -->
      @if (pagedResult.items.length === 0 && searchTerm.length > 0) {
        <div class="empty-container">
          <div class="empty-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </div>
          <h2 class="empty-title">No Results Found</h2>
          <p class="empty-message">
            No companies match your current search criteria.
          </p>
          <button class="clear-search-action" (click)="clearSearch()">
            Clear Search
          </button>
        </div>
      }
      <!-- Empty State - No Data -->
      @if (pagedResult.items.length === 0 && searchTerm.length === 0) {
        <div class="empty-container">
          <div class="empty-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              <polyline points="9 22 9 12 15 12 15 22"></polyline>
            </svg>
          </div>
          <h2 class="empty-title">No Companies Found</h2>
          <p class="empty-message">
            There are no companies to display at this time.
          </p>
        </div>
      }
      <!-- Grid with Data -->
      @if (pagedResult.items.length > 0) {
        <div class="grid-container" [class.is-loading]="loading$ | async">
          <!-- Loading Overlay -->
          @if (loading$ | async) {
            <div class="grid-loading-overlay">
              <ukg-progress-circular></ukg-progress-circular>
            </div>
          }
          <!-- Results Summary -->
          <div class="grid-toolbar">
            <div class="results-info-row">
              <span class="results-text">
                Showing
                {{ (pagedResult.pageNumber - 1) * pagedResult.pageSize + 1 }} -
                {{
                  Math.min(
                    pagedResult.pageNumber * pagedResult.pageSize,
                    pagedResult.totalCount
                  )
                }}
                of {{ pagedResult.totalCount }} companies
              </span>
            </div>
          </div>
          <!-- Table with Data -->
          <!-- eslint-disable-next-line @angular-eslint/template/interactive-supports-focus -->
          <div
            class="table-scroll-container"
            (click)="handleTableClick($event)"
            (keydown)="handleTableClick($event)">
            <ukg-data-table>
              <table>
                <thead>
                  <tr>
                    @for (col of visibleColumns; track col) {
                      <th [style.width]="col.width">
                        <button
                          class="sortable-header"
                          (click)="
                            onTableSort({ key: col.field, isAsc: 'none' })
                          "
                          type="button">
                          {{ col.header }}
                          <span class="sort-icon">{{
                            getSortIcon(col.field)
                          }}</span>
                        </button>
                      </th>
                    }
                  </tr>
                </thead>
                <tbody>
                  @for (row of filteredTableData$ | async; track row) {
                    <tr
                      [attr.role]="'button'"
                      [attr.tabindex]="0"
                      (keydown.enter)="handleTableClick($event)"
                      (keydown.space)="handleTableClick($event)">
                      @for (col of visibleColumns; track col) {
                        <td [style.width]="col.width">
                          {{ row.data[col.field] }}
                        </td>
                      }
                    </tr>
                  }
                </tbody>
              </table>
            </ukg-data-table>
          </div>
          <!-- Pagination -->
          <div class="pagination-container">
            <div class="page-size-selector">
              <label for="pageSize">Rows per page:</label>
              <select
                id="pageSize"
                [value]="pagedResult.pageSize"
                (change)="onPageSizeChange(+$any($event.target).value)">
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
            <div class="pagination-info">
              Page {{ pagedResult.pageNumber }} of {{ pagedResult.totalPages }}
            </div>
            <div class="pagination-controls">
              <button
                class="pagination-btn"
                [disabled]="!pagedResult.hasPreviousPage"
                (click)="onPageChange(1)"
                aria-label="First page">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2">
                  <polyline points="11 17 6 12 11 7"></polyline>
                  <polyline points="18 17 13 12 18 7"></polyline>
                </svg>
              </button>
              <button
                class="pagination-btn"
                [disabled]="!pagedResult.hasPreviousPage"
                (click)="onPageChange(pagedResult.pageNumber - 1)"
                aria-label="Previous page">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>
              <button
                class="pagination-btn"
                [disabled]="!pagedResult.hasNextPage"
                (click)="onPageChange(pagedResult.pageNumber + 1)"
                aria-label="Next page">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
              <button
                class="pagination-btn"
                [disabled]="!pagedResult.hasNextPage"
                (click)="onPageChange(pagedResult.totalPages)"
                aria-label="Last page">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2">
                  <polyline points="13 17 18 12 13 7"></polyline>
                  <polyline points="6 17 11 12 6 7"></polyline>
                </svg>
              </button>
            </div>
          </div>
        </div>
      }
    }
  }
</div>
